import { Crown, Target, Check, AlertTriangle } from "lucide-react";

const KING_RULES = [
  { label: "Objetivo", text: "Ocupar la posición #1 de la cartelera al momento del cierre del concurso." },
  { label: "Quiénes participan", text: "Cualquier usuario que registre el enlace de su red social y realice una puja económica en pesos mexicanos (MXN)." },
  { label: "Mecánica", text: "La lista se ordena automáticamente de mayor a menor según el monto total acumulado por cada perfil. Cuando haces un ofrecimiento o puja con un monto mayor al que tenga el perfil que se encuentre en el Ranking número 1, tú tomas ese lugar mientras no exista otra puja mayor que te desplace. Puedes realizar múltiples pujas durante las 72 horas para recuperar o defender tu puesto." },
  { label: "Regla Anti-Trampas (Anti-Sniping)", text: "Toda puja realizada dentro de los últimos 2 minutos previos al cierre extenderá el temporizador automáticamente por 60 segundos adicionales. Esto se repetirá de forma indefinida hasta que no haya más ofertas." },
  { label: "Duración y Cierre", text: "Cada concurso tiene una duración de 72 horas contadas desde el momento exacto en que el administrador presiona 'Iniciar'. La hora final es dictada estrictamente por el servidor central de la plataforma." },
  { label: "El Premio", text: "El dueño del perfil que quede congelado en el puesto #1 se llevará de forma automática el 50% de todo el pozo de dinero acumulado durante las 72 horas. El pago se procesará en un máximo de 48 horas a la cuenta registrada." },
  { label: "Política", text: "Los montos aportados en las pujas son definitivos y no reembolsables. Perfiles con contenido ilegal, fraudulento o explícito serán eliminados sin derecho a reclamo ni devolución.", warning: true },
];

const HUNTER_RULES = [
  { label: "Objetivo", text: "Acumular la mayor cantidad de puntos de viralización durante las 72 horas del concurso compartiendo los perfiles de la cartelera." },
  { label: "Quiénes participan", text: "Cualquier usuario que se registre de forma gratuita en la sección \"Cazador\" ingresando su correo y datos de cobro (PayPal o cuenta bancaria)." },
  { label: "Mecánica", text: "Utiliza el botón de WhatsApp dentro de la plataforma para generar tus enlaces únicos de rastreo y compártelos con tus contactos o grupos. Cada persona que haga clic en tu enlace sumará +1 punto de viralización a tu cuenta." },
  { label: "Sistema de Validación", text: "Para garantizar la transparencia, el sistema solo contabiliza un (1) clic por dirección IP o dispositivo único al día. Los clics repetidos desde un mismo teléfono o generados por software automático (bots) serán anulados inmediatamente." },
  { label: "Duración y Cierre", text: "Cada concurso dura 72 horas y cierra en sincronía con el ranking principal. En caso de empate en puntos, el premio se otorgará al usuario que haya alcanzado la puntuación primero." },
  { label: "El Premio", text: "El Cazador que termine en el puesto #1 del miniranking se llevará el 10% de todo el dinero acumulado en el pozo global. El depósito se realizará en un plazo máximo de 48 horas hábiles." },
];

function RulesCard({ title, subtitle, badge, badgeColor, accent, Icon, rules, testid }) {
  return (
    <article
      data-testid={testid}
      className="relative rounded-2xl border bg-void-card p-6 sm:p-8 overflow-hidden"
      style={{ borderColor: `${accent}40` }}
    >
      <div
        className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-15 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 60%)` }}
        aria-hidden
      />

      <header className="relative mb-6 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{
              backgroundColor: `${accent}18`,
              border: `1px solid ${accent}50`,
              color: accent,
            }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div
              className="text-[10px] font-mono uppercase tracking-[0.28em] mb-1"
              style={{ color: accent }}
            >
              {subtitle}
            </div>
            <h3 className="font-heading font-black text-xl sm:text-2xl leading-tight tracking-tight">
              {title}
            </h3>
          </div>
        </div>
        <span
          className="hidden sm:inline-flex items-center px-3 py-1 rounded-full text-xs font-mono uppercase tracking-widest flex-shrink-0"
          style={{
            backgroundColor: `${accent}15`,
            border: `1px solid ${accent}45`,
            color: accent,
          }}
        >
          {badge}
        </span>
      </header>

      <ul className="relative space-y-4">
        {rules.map((r, i) => (
          <li key={i} className="flex items-start gap-3">
            <div
              className="mt-0.5 w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: r.warning ? "#FF003C22" : `${accent}18`,
                color: r.warning ? "#FF003C" : accent,
              }}
            >
              {r.warning ? <AlertTriangle className="w-3 h-3" /> : <Check className="w-3 h-3" />}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[11px] font-mono uppercase tracking-widest mb-1"
                style={{ color: r.warning ? "#FF003C" : accent }}
              >
                {r.label}
              </div>
              <p className="text-sm text-white/85 leading-relaxed">{r.text}</p>
            </div>
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function RulesSection() {
  return (
    <section id="rules" data-testid="rules-section" className="mt-20">
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-neon-cyan mb-2">
          BASES OFICIALES · REGLAMENTO
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
          Cómo se juega y cómo se paga
        </h2>
        <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
          Antes de pujar o cazar, lee las bases oficiales de cada modalidad. Cada concurso
          dura <span className="text-white font-bold">72 horas</span> exactas y se resuelve
          automáticamente en el servidor cuando el temporizador llega a cero.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <RulesCard
          testid="rules-king-box"
          title="BASES DE PUJA POR TU PERFIL"
          subtitle="El Rey del Ranking"
          badge="50% del Pozo"
          accent="#FFB800"
          Icon={Crown}
          rules={KING_RULES}
        />
        <RulesCard
          testid="rules-hunter-box"
          title="BASES PARA PARTICIPAR COMO VIRALIZADOR"
          subtitle="El Cazador Viral"
          badge="10% del Pozo"
          accent="#FF003C"
          Icon={Target}
          rules={HUNTER_RULES}
        />
      </div>

      <p className="mt-6 text-[11px] font-mono text-muted-foreground text-center uppercase tracking-widest">
        Servidor central UTC · Payouts encriptados · Cada concurso dura 72h
      </p>
    </section>
  );
}
